import { supabase } from '@/integrations/supabase/client';

export interface ConditionalSkipBulkData {
  bot_name: string;
  enabled: boolean;
  loss_streak_threshold: number;
  max_loss_streak?: number | null;
  max_loss_behavior: 'reset_streak' | 'lock_gate';
  forward_url: string;
}

export interface BotInfo {
  id: string;
  name: string;
  type: string;
  status: string;
}

export class ConditionalSkipService {
  static async getBotByName(name: string): Promise<BotInfo | null> {
    const { data, error } = await supabase
      .from('bots')
      .select('id, name, type, status')
      .ilike('name', name.trim())
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }
    
    return data;
  }

  static async checkExistingSetting(botId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('conditional_skip_settings')
      .select('id')
      .eq('bot_id', botId)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      throw error;
    }
    
    return data !== null;
  }

  static async createBulkSetting(bulkData: ConditionalSkipBulkData): Promise<any> {
    // Step 1: Find bot by name
    const bot = await this.getBotByName(bulkData.bot_name);
    if (!bot) {
      throw new Error(`Bot "${bulkData.bot_name}" not found`);
    }

    // Step 2: Check if bot already has conditional skip settings
    const hasExistingSetting = await this.checkExistingSetting(bot.id);
    if (hasExistingSetting) {
      throw new Error(`Bot "${bulkData.bot_name}" already has conditional skip settings`);
    }

    // Step 3: Create the setting
    const { data, error } = await supabase
      .from('conditional_skip_settings')
      .insert({
        bot_id: bot.id,
        enabled: bulkData.enabled,
        loss_streak_threshold: bulkData.loss_streak_threshold,
        max_loss_streak: bulkData.max_loss_streak,
        max_loss_behavior: bulkData.max_loss_behavior,
        forward_url: bulkData.forward_url
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create settings for bot "${bulkData.bot_name}": ${error.message}`);
    }

    return data;
  }

  static async bulkCreateSettings(bulkDataArray: ConditionalSkipBulkData[]): Promise<{
    successCount: number;
    errorCount: number;
    errors: { botName: string; error: string }[];
  }> {
    let successCount = 0;
    let errorCount = 0;
    const errors: { botName: string; error: string }[] = [];

    for (const bulkData of bulkDataArray) {
      try {
        await this.createBulkSetting(bulkData);
        successCount++;
      } catch (error: any) {
        errorCount++;
        errors.push({
          botName: bulkData.bot_name,
          error: error.message || 'Unknown error'
        });
      }
    }

    return {
      successCount,
      errorCount,
      errors
    };
  }

  static validateBulkData(data: Partial<ConditionalSkipBulkData>): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate bot name
    if (!data.bot_name?.trim()) {
      errors.push('Bot name is required');
    }

    // Validate threshold
    if (data.loss_streak_threshold === undefined || data.loss_streak_threshold === null) {
      errors.push('Loss streak threshold is required');
    } else if (data.loss_streak_threshold < 0) {
      errors.push('Loss streak threshold must be non-negative');
    }

    // Validate max loss streak (optional)
    if (data.max_loss_streak !== undefined && data.max_loss_streak !== null && data.max_loss_streak <= 0) {
      errors.push('Max loss streak must be positive if provided');
    }

    // Validate max loss behavior
    if (data.max_loss_behavior && !['reset_streak', 'lock_gate'].includes(data.max_loss_behavior)) {
      errors.push('Max loss behavior must be "reset_streak" or "lock_gate"');
    }

    // Validate forward URL
    if (!data.forward_url?.trim()) {
      errors.push('Forward URL is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Bulk delete all Type 1 (START_STOP_AFTER_LOSS) conditional skip settings
   * This also cascades to related tables
   */
  static async bulkDeleteType1Settings(): Promise<{
    deletedCount: number;
    error: string | null;
  }> {
    try {
      // First, get all Type 1 bot IDs for cleanup
      const { data: type1Settings, error: fetchError } = await supabase
        .from('conditional_skip_settings')
        .select('id, bot_id')
        .eq('skip_type', 'START_STOP_AFTER_LOSS');

      if (fetchError) throw fetchError;
      
      const botIds = type1Settings?.map(s => s.bot_id) || [];
      
      // Delete from conditional_skip_settings (main table)
      const { error: deleteError, count } = await supabase
        .from('conditional_skip_settings')
        .delete()
        .eq('skip_type', 'START_STOP_AFTER_LOSS')
        .select();

      if (deleteError) throw deleteError;

      // Clean up bot_current_condition_skip states
      if (botIds.length > 0) {
        await supabase
          .from('bot_current_condition_skip')
          .delete()
          .in('bot_id', botIds);
      }

      return {
        deletedCount: count || 0,
        error: null
      };
    } catch (error: any) {
      console.error('Error bulk deleting Type 1 settings:', error);
      return {
        deletedCount: 0,
        error: error.message
      };
    }
  }
}